import React, { useRef } from "react";
import {
  UserOutlined,
  StarFilled,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Avatar, Carousel, Grid } from "antd";
import { formatDate } from "../../../utils/dateFormatter";

const { useBreakpoint } = Grid;

const Feedback = ({ isDarkMode, reviews = [] }) => {
  const carouselRef = useRef(null);
  const screens = useBreakpoint();

  // Control slides based on screen size
  const slidesToShow = screens.lg ? 3 : screens.md ? 2 : 1;

  // Star rating component
  const StarRating = ({ rating }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <StarFilled
          key={i}
          className={i <= rating ? "text-yellow-400" : "text-gray-300 "}
          style={{ fontSize: 12 }}
        />
      ))}
    </div>
  );

  const next = () => carouselRef.current?.next();
  const prev = () => carouselRef.current?.prev();

  // Determine background based on mode and screen
  const getContainerBg = () => {
    if (isDarkMode) {
      return "bg-gray-900"; // Dark mode always dark gray
    }
    // Light mode
    return "bg-rose-300 md:bg-gray-100";
  };

  // Calculate average rating from reviews
  const calculateAverageRating = () => {
    if (!reviews || reviews.length === 0) return 0;
    const total = reviews.reduce(
      (sum, review) => sum + (review.rating || 0),
      0,
    );
    return (total / reviews.length).toFixed(1);
  };

  // Empty state
  const EmptyState = () => (
    <div className="py-12 text-center">
      <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full">
        <StarFilled className="text-2xl text-gray-400" />
      </div>
      <p className="text-gray-500 dark:text-gray-300">No reviews yet</p>
    </div>
  );

  if (!reviews || reviews.length === 0) {
    return (
      <section>
        <div
          className={`relative mt-5 overflow-hidden rounded-2xl ${getContainerBg()}`}
        >
          <div className="relative z-10 p-4 md:p-8">
            <div
              className={`flex items-center justify-between px-3 py-2 mb-2 border-b rounded-t-xl ${
                isDarkMode
                  ? "border-gray-700 bg-gray-800"
                  : "border-rose-500 md:border-gray-200 bg-white md:bg-transparent"
              }`}
            >
              <div className="flex items-center gap-2">
                <StarRating rating={5} />
                <span
                  className={`text-sm font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}
                >
                  0.0
                </span>
              </div>
              <a
                href="https://search.google.com/local/writereview?placeid=ChIJVxypniAZDTkRIsF0G1FNzOY"
                target="_blank"
                className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded transition-colors duration-200 ${
                  isDarkMode
                    ? "bg-blue-900/40 text-blue-300 hover:bg-blue-900/60"
                    : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                }`}
              >
                <img
                  src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png"
                  alt="Google"
                  className="w-3 h-3"
                />
                Review us
              </a>
            </div>
            <EmptyState />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div
        className={`relative mt-5 overflow-hidden rounded-2xl ${getContainerBg()}`}
      >
        <div className="relative z-10 p-4 md:p-8">
          {/* Header */}
          <div
            className={`
            flex items-center justify-between px-3 py-2 mb-2 border-b rounded-t-xl
            ${
              isDarkMode
                ? "border-gray-700 bg-gray-800"
                : "border-rose-500 md:border-gray-200 bg-white md:bg-transparent"
            }
          `}
          >
            <div className="flex items-center gap-2">
              <StarRating rating={calculateAverageRating()} />
              <span
                className={`text-sm font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}
              >
                {calculateAverageRating()}
              </span>
            </div>

            <a
              href="https://search.google.com/local/writereview?placeid=ChIJVxypniAZDTkRIsF0G1FNzOY"
              target="_blank"
              className={`
                flex items-center gap-1 px-2 py-1 text-[10px] rounded
                ${
                  isDarkMode
                    ? "bg-blue-900/40 text-blue-300 hover:bg-blue-900/60"
                    : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                }
                transition-colors duration-200
              `}
            >
              <img
                src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png"
                alt="Google"
                className="w-3 h-3"
              />
              Review us
            </a>
          </div>

          {/* Carousel */}
          <div className="relative group">
            <Carousel
              ref={carouselRef}
              dots={false}
              autoplay
              autoplaySpeed={4000}
              slidesToShow={Math.min(slidesToShow, reviews.length)}
              slidesToScroll={1}
            >
              {reviews.map((review) => (
                <div key={review.id} className="px-1 pb-2">
                  <div
                    className={`p-3 h-full rounded-xl border transition-all duration-200 hover:shadow-lg ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-750"
                        : "bg-white border-gray-200 text-gray-700 hover:shadow-md"
                    }`}
                  >
                    {/* User */}
                    <div className="flex items-center gap-2 mb-2">
                      <Avatar
                        size={32}
                        icon={<UserOutlined />}
                        className={`
                          ${
                            isDarkMode
                              ? "bg-gray-700 text-gray-300"
                              : "bg-gray-200 text-gray-600"
                          }
                        `}
                      />
                      <div>
                        <p
                          className={`text-xs font-medium truncate max-w-[120px] ${isDarkMode ? "text-white" : "text-gray-800"}`}
                        >
                          {review.name || review.user?.name || "Anonymous"}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {formatDate(review.time || review.created_at)}
                        </p>
                      </div>
                    </div>

                    {/* Rating */}
                    <StarRating rating={review.rating || 5} />

                    {/* Comment */}
                    <p
                      className={`text-[11px] mt-2 line-clamp-3 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                    >
                      "{review.comment || review.comment || "Great service!"}"
                    </p>

                    {/* Product Info (optional) */}
                    {review.product_name && (
                      <p
                        className={`mt-2 text-[10px] ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}
                      >
                        📱 {review.product_name}
                      </p>
                    )}

                    {/* Helpful */}
                    <p
                      className={`mt-2 text-[10px] ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}
                    >
                      👍 {review.helpful || 0} found helpful
                    </p>
                  </div>
                </div>
              ))}
            </Carousel>

            {/* Desktop Arrows */}
            {reviews.length > slidesToShow && (
              <>
                <button
                  onClick={prev}
                  className={`
                    absolute left-0 items-center justify-center hidden w-8 h-8 -ml-4 
                    -translate-y-1/2 rounded-full shadow opacity-0 md:flex top-1/2 
                    group-hover:opacity-100 hover:scale-110 transition-all duration-200
                    ${
                      isDarkMode
                        ? "bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700"
                        : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }
                  `}
                >
                  <LeftOutlined className="text-xs" />
                </button>

                <button
                  onClick={next}
                  className={`
                    absolute right-0 items-center justify-center hidden w-8 h-8 -mr-4 
                    -translate-y-1/2 rounded-full shadow opacity-0 md:flex top-1/2 
                    group-hover:opacity-100 hover:scale-110 transition-all duration-200
                    ${
                      isDarkMode
                        ? "bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700"
                        : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }
                  `}
                >
                  <RightOutlined className="text-xs" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Feedback;
